/*
 * Description:
 * ---------------------
 * One rule's details, the same on the master map and in a box's focused
 * view: the address the source box connects with, to copy; its setup; the
 * traffic its two boxes observed; the address check for an address inside the
 * source's own network; that every device at the source may connect, on any
 * port, and that access the other way needs its own permission; and what can
 * be done: Manage access, and behind More actions, pausing the device and
 * removing this box's access. Each apart, so a rule saved never reads as one
 * that works. mine and theirs are the source's and the owner's reads of
 * /reach. Wording lives in reachWords.js.
 *
 * ! file components/RuleDetails.js
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCopy, FiMoreHorizontal } from "react-icons/fi";
import * as W from "./reachWords";

export default function RuleDetails({
  rule, site, mine, theirs, anchors, canWrite, busy, onRefresh, onManage, onToggle, onRemove, onFocus,
}) {
  const e = rule.entry;
  const setup = W.setupOf(e, rule.box, anchors);
  const check = setup.state === "applied" ? W.addressCheckOf(e, rule.box, rule.address, site, mine) : null;
  return (
    <div className="rd-rule" role="region" aria-label={`${e.name} from ${rule.box}`}>
      <p className="rd-rule-use">
        <span>{W.connectFrom(rule.box)}</span>
        <Copyable text={rule.address} />
        {W.isRange(e.lan_address) && <span className="rd-quiet">{W.rangeNote(e.lan_address, rule.address)}</span>}
      </p>
      <dl className="rd-facts">
        <div>
          <dt>{W.SETUP_LABEL}</dt>
          <dd><b className={`is-${setup.state}`}>{W.SETUP_WORDS[setup.state]}</b>{setup.text && <span>{setup.text}</span>}</dd>
        </div>
        <div>
          <dt>{W.TRAFFIC_LABEL}</dt>
          <dd>
            <span>{W.trafficOf(e, rule.box, mine, theirs)}</span>
            {mine?.at && <span className="rd-quiet">{W.asOf(mine.at)}</span>}
            <button type="button" className="rd-link" onClick={onRefresh}>{W.REFRESH}</button>
          </dd>
        </div>
        {check && (
          <div>
            <dt>{W.ADDRESS_LABEL}</dt>
            <dd><b className={`is-${check.state}`}>{W.ADDRESS_WORDS[check.state]}</b><span>{check.text}</span></dd>
          </div>
        )}
      </dl>
      <p className="rd-quiet">{W.scopeLine(rule.box)} {W.reverseLine(e.site)}</p>
      {(canWrite || onFocus) && (
        <div className="rd-acts">
          {canWrite && <button type="button" className="dl-btn rd-small" disabled={busy} onClick={onManage}>{W.MANAGE}</button>}
          {onFocus && <button type="button" className="dl-btn rd-small" onClick={onFocus}>{W.focusOn(rule.box)}</button>}
          {canWrite && (
            <MoreMenu disabled={busy} items={[
              { label: e.disabled ? W.RESUME_ALL : W.PAUSE_ALL, onPick: onToggle },
              { label: W.removeAccess(rule.box), onPick: onRemove, danger: true },
            ]} />
          )}
        </div>
      )}
    </div>
  );
}

/* More actions: a button opening a short menu of the rarer actions, on the
   page beside the button (above it near the window's foot), closed by picking
   one, by Escape, by scrolling, or by a click anywhere else. items are
   [{label, onPick, danger}]. */
const MENU_ROOM = 140;
export function MoreMenu({ items, disabled }) {
  const [at, setAt] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!at) return undefined;
    const close = () => setAt(null);
    const away = (e) => !ref.current?.contains(e.target) && close();
    const escape = (e) => e.key === "Escape" && close();
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [at]);
  const toggle = (e) => {
    if (at) return setAt(null);
    const r = e.currentTarget.getBoundingClientRect();
    const up = r.bottom + MENU_ROOM > window.innerHeight;
    return setAt({ left: r.left, top: up ? r.top - 6 : r.bottom + 6, up });
  };
  return (
    <span className="rd-menu-at" ref={ref}>
      <button type="button" className="dl-btn icon rd-small" aria-label={W.MORE} aria-haspopup="menu" aria-expanded={!!at}
        disabled={disabled} onClick={toggle}>
        <FiMoreHorizontal size={15} />
      </button>
      {at && (
        <div className={`rd-menu${at.up ? " is-up" : ""}`} role="menu" style={{ left: at.left, top: at.top }}>
          {items.map((item) => (
            <button key={item.label} type="button" role="menuitem" className={item.danger ? "is-danger" : ""}
              onClick={() => {
                setAt(null);
                item.onPick();
              }}>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

export function Copyable({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      setDone(false);
    }
  };
  return (
    <span className="rd-copy">
      <b className="rd-mono">{text}</b>
      <button type="button" onClick={copy} aria-label={`Copy ${text}`} title={done ? "Copied" : "Copy"}>
        {done ? "Copied" : <FiCopy size={12} />}
      </button>
    </span>
  );
}
